import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchProduceditems,
    createProducedItem,
    updateProducedItem,
    deleteProducedItem,
    downloadProducedItem,
    setPagination,
    setSort,
    searchProduceditems,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/produceditem/produceditemActions';
import ProducedItemForm from '../../components/pageModule/Produceditems/ProducedItemForm';
import CloudActionButtons, { isContactCloudLocked } from '../../components/pageModule/Customers/CloudActionButtons';
import produceditemService from '../../services/produceditemService';

const MySwal = withReactContent(Swal);

const Produceditems = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = []
}) => {
    const produceditemState = useSelector(state => state.produceditem);
    const dispatch = useDispatch();
    const [cloudLoadingByItem, setCloudLoadingByItem] = useState({});

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createProducedItem(formData));
            toast.success('ProducedItem created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating produceditem', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateProducedItem(id, formData));
            toast.success('ProducedItem updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating produceditem', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteProducedItem(id));
                toast.success('ProducedItem deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting produceditem', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleDownload = async (type) => {
        try {
            const response = await dispatch(downloadProducedItem(type));
            window.open(response.url, '_blank')
            return response;
        } catch (error) {
            toast.error(error.message || 'Error downloading produceditems', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    const handleCloudPush = useCallback(async (row) => {
        if (!row?.id) {
            return;
        }

        if (isContactCloudLocked(row?.cloud)) {
            toast.warning('Produced item cloud sync is locked', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return;
        }

        setCloudLoadingByItem(prev => ({ ...prev, [row.id]: true }));
        try {
            const response = await produceditemService.pushCloud(row.id, {});
            toast.success(response.message || 'Produced item queued for cloud sync', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            await dispatch(fetchProduceditems({}, {}, true, externalCondition));
        } catch (error) {
            toast.error(error.message || 'Error pushing produced item to cloud', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setCloudLoadingByItem(prev => ({ ...prev, [row.id]: false }));
        }
    }, [dispatch, externalCondition]);

    const columns = useMemo(() => [
        {
            id: 'produced_date',
            name: 'Produced Date',
            selector: row => row.produced_date ?? '-',
        },
        {
            id: 'order_number',
            name: 'Job Order Number',
            selector: row => row.order_number ?? '-',
        },
        {
            id: 'order_date',
            name: 'Order Date',
            selector: row => row.order_date ?? '-',
        },
        {
            id: 'trial_date',
            name: 'Trial Date',
            selector: row => row.trial_date ?? '-',
        },
        {
            id: 'due_date',
            name: 'Due Date',
            selector: row => row.due_date ?? '-',
        },
        {
            id: 'customer_name',
            name: 'Customer Name',
            selector: row => row.customer_name ?? '-',
            minWidth: '180px',
        },
        {
            id: 'lpo_number',
            name: 'LPO Number',
            selector: row => row.lpo_number ?? '-',
            minWidth: '180px',
        },
        {
            id: 'item_name',
            name: 'Item Name',
            selector: row => row.item_name ?? '-',
            minWidth: '180px',
        },
        {
            id: 'employee_name',
            name: 'Employee Name',
            selector: row => row.employee_name ?? '-',
            minWidth: '180px',
        },
        {
            id: 'tailor_id',
            name: 'Tailor ID',
            selector: row => row.tailor_id ?? '-',
        },
        {
            id: 'tailor_name',
            name: 'Tailor Name',
            selector: row => row.tailor_name ?? '-',
            minWidth: '180px',
        },
        {
            id: 'quantity',
            name: 'Quantity',
            selector: row => row.quantity ?? '-',
        },
        {
            id: 'produced_qty',
            name: 'Produced Qty',
            selector: row => row.produced_qty ?? '-',
        },
        {
            id: 'fg_unit_price',
            name: 'Unit Price',
            selector: row => row.fg_unit_price ?? '-',
            minWidth: '100px',
        },
        {
            id: 'unit_percentage',
            name: 'Unit Percentage',
            selector: row => row.unit_percentage ?? '-',
            minWidth: '100px',
        },
        {
            id: 'total_price',
            name: 'Total Price',
            selector: row => row.total_price ?? '-',
            minWidth: '100px',
        },
        {
            id: 'issue_number',
            name: 'Issue Number',
            selector: row => row.issue_number ?? '-',
            minWidth: '180px',
        },
        {
            id: 'issue_date',
            name: 'Issue Date',
            selector: row => row.issue_date ?? '-',
            minWidth: '100px',
        },
        // {
        //     id: 'cloud',
        //     name: 'Cloud',
        //     minWidth: '140px',
        //     cell: (row) => (
        //         <CloudActionButtons
        //             row={row}
        //             onPush={handleCloudPush}
        //             isPushing={!!cloudLoadingByItem[row?.id]}
        //         />
        //     ),
        //     ignoreRowClick: true,
        //     allowOverflow: true,
        // },
    ], [handleCloudPush, cloudLoadingByItem]);


    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchProduceditems(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        download: handleDownload,
        search: (search) => dispatch(searchProduceditems(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    const defaultAvailableActions = ['search', 'filter'];
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Produced Items"
                moduleName="ProducedItem"
                moduleKey="produceditems"
                state={produceditemState}
                actions={actions}
                FormComponent={ProducedItemForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={false}
                onSelectedRowsChange={onSelectedRowsChange}
                onClickedRow={onClickedRow}
                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Produceditems;
